import { Factory } from "@/data/dao/Factory";
import { DocumentDao } from "@/data/dao/service/DocumentDao";
import type { IDocument } from "@/data/model/Document";
import { ServiceType } from "@/data/model/Enums";
import type { IFileInfo } from "@/data/model/System";
import { Format } from "@/data/util/Format";



import { clientLanguage, translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";



import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";
import { ServicesIcon } from "@/app/ui/ServicesIcon";
import { UserMenu } from "@/app/ui/UserMenu";


export class DocumentsController extends BaseController {
    private $head: JQuery;
    private $documents: JQuery;
    public userMenu: UserMenu;

    public onShowServices?: { () };

    private needsReload = true;
    private lastReload?: Date;

    constructor() {
        super("documents");

        this.$head = $('<div class="head"><div class="title"></div></div>').appendTo(this.$view);

        new ServicesIcon().appendTo(this.$head).onClick = () => {
            if (this.onShowServices !== undefined) this.onShowServices();
        };

        this.userMenu = new UserMenu().appendTo(this.$head);

        this.$documents = $('<div class="scrollable" />').appendTo(this.$view);

        TastierClient.context.onAllocationChange.add((alloc) => {
            if (alloc === undefined) return;

            this.updateTitle();

            if (this.isVisible!) this.loadDocuments();
            else this.needsReload = true;
        });

        this.updateTitle();
    }

    protected onBecomeVisible() {
        this.loadDocuments(true);

        this.resize();
        $(window).bind("resize.documents", () => this.resize());
    }

    protected onBecomeInvisible() {
        $(window).unbind("resize.documents");
        this.$documents.find("> .grid.shown").removeClass("shown");
    }

    private async loadDocuments(onlyIfNeeded?: boolean) {
        if (onlyIfNeeded !== undefined && onlyIfNeeded === true && !this.shouldReload()) {
            //just fade in docs
            this.$documents.find("> .grid").each((i, e) => {
                const aee = $(e);

                aee.delay(i * 90).queue((e) => {
                    aee.addClass("shown");
                    e();
                });
            });

            return;
        }

        this.lastReload = new Date();
        this.needsReload = false;

        this.$documents.html('<div class="load"><div class="loader"></div>' + trans.documents.loading + "</div>");
        try {
            const documents = await Factory.getInstance<DocumentDao>(DocumentDao).getActiveDocuments();
            this.$documents.empty();

            if (documents.length === 0) {
                this.$documents.append('<div class="nodata">' + trans.documents.noData + "</div>");
                return;
            }

            for (const i in documents) this.addDocument(documents[i], parseInt(i) * 90);

            this.resize();
        } catch (ex) {
            logError(ex);
            this.$documents.empty();
            Popup.show(trans.documents.failedToLoad);
        }
    }

    private addDocument(doc: IDocument, withDelay?: number) {
        let name = translate(doc.displayName);
        if (name == null || name.length === 0)
            name = doc.name;

        const grid = $('<div class="grid"></div>').appendTo(this.$documents);
        const c = $(
            '<div class="document"><div class="center"><div class="name">' +
                Format.htmlEscape(name) +
                "</div></div></div>"
        ).appendTo(grid);

        Ripple.bind(
            c.click((e) => {
                e.preventDefault();
                this.downloadDocument($(e.currentTarget).data("model"));
            })
        ).data("model", doc);

        if (withDelay === undefined) grid.addClass("shown");
        else {
            grid.delay(withDelay).queue((e) => {
                grid.addClass("shown");
                e();
            });
        }
    }

    private downloadDocument(doc: IDocument) {
        const f = Format.translateProperty<IFileInfo>(doc, "file", clientLanguage());
        if (f !== undefined) window.open(f.path + "?c=" + new Date().getTime());
    }

    private shouldReload() {
        return (
            this.needsReload === true ||
            (this.lastReload !== undefined && new Date().getTime() - this.lastReload.getTime() > 5 * 60 * 1000)
        );
    }

    private updateTitle() {
        const t = this.$head.find("> .title");
        const service =
            TastierClient.context.currentAllocation === undefined
                ? undefined
                : TastierClient.context.currentAllocation.services.find((c) => c.serviceType === ServiceType.Documents);
        if (service === undefined) {
            t.empty();
            return;
        }

        let name: string = Format.translate(service.name, clientLanguage());
        if (name === undefined) name = trans.enums.serviceType[ServiceType[service.serviceType]];

        t.html(Format.htmlEscape(name)!);
    }

    private resize() {
        const ratio = 144 / 188;
        const newHeight = ((this.$documents.width() - 12) / 2) * ratio;
        this.$documents.find("> .grid").each((i, e) => {
            $(e).height(newHeight);
        });
    }
}
